import React, { useContext, useMemo } from 'react';
import Select from 'react-select';

import ColorPicker from './ColorPicker';
import { LanguageContext } from '../context/LanguageContext';
import commonTranslations from '../languages/common.json';
import {
  getThemeColorKey,
  getThemeColorValue,
  normalizeThemeMode,
} from '../utils/themeColors';


const TextTitleSettings = ({ edit, setEdit, alignments = [] }) => {
  const { lang } = useContext(LanguageContext);

  const t = (commonTranslations?.[lang]?.common?.textTitleSettings || {}) ?? {};

  const alignmentOptions = useMemo(() => {
    if (Array.isArray(alignments) && alignments.length > 0) return alignments;
    return [
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'right' },
    ];
  }, [alignments]);

  const fontFamilies = useMemo(
    () => [
      { label: 'Inherit', value: '' },
      { label: 'Arial', value: 'Arial, sans-serif' },
      { label: 'Inter', value: 'Inter, system-ui, sans-serif' },
      { label: 'Roboto', value: 'Roboto, system-ui, sans-serif' },
      { label: 'Poppins', value: 'Poppins, system-ui, sans-serif' },
      { label: 'Montserrat', value: 'Montserrat, system-ui, sans-serif' },
      { label: 'Courier New', value: '"Courier New", monospace' },
    ],
    []
  );

  const fontWeights = useMemo(
    () => [
      { label: 'Inherit', value: '' },
      { label: 'Normal (400)', value: '400' },
      { label: 'Medium (500)', value: '500' },
      { label: 'SemiBold (600)', value: '600' },
      { label: 'Bold (700)', value: '700' },
      { label: 'ExtraBold (800)', value: '800' },
    ],
    []
  );

  const fontStyles = useMemo(
    () => [
      { label: 'Normal', value: 'normal' },
      { label: 'Italic', value: 'italic' },
    ],
    []
  );

  const decorations = useMemo(
    () => [
      { label: 'None', value: 'none' },
      { label: 'Underline', value: 'underline' },
      { label: 'Line-through', value: 'line-through' },
      { label: 'Overline', value: 'overline' },
    ],
    []
  );

  const updateProps = (patch) =>
    setEdit((prev) => ({
      ...prev,
      props: {
        ...prev.props,
        ...patch,
      },
    }));

  const themeMode = normalizeThemeMode(edit?.props?.themeMode);
  const isLight = themeMode === 'light';

  const titleValue = edit?.props?.title ?? '';
  const titleSize = Number(edit?.props?.titleSize ?? 20);
  const color = getThemeColorValue(edit?.props, 'color', themeMode, '#00f0f0');
  const alignment = edit?.props?.alignment ?? 'left';
  const fontFamily = edit?.props?.fontFamily ?? '';
  const fontWeight = edit?.props?.fontWeight ?? '';
  const fontStyle = edit?.props?.fontStyle ?? 'normal';
  const textDecoration = edit?.props?.textDecoration ?? 'none';

  return (
    <div className="edit-content-part" style={{ flex: 0.65 }}>
      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.titleLabel || 'Title'}
        </label>
        <textarea
          className="add-column"
          rows={3}
          placeholder={t.titlePlaceholder || 'Enter title text'}
          style={{
            padding: '8px 10px',
            resize: 'vertical',
            color: 'var(--cell--color)',
            caretColor: 'var(--cell--color)',
            backgroundColor: 'var(--part-background)',
            border: '1px solid var(--header-background)',
            borderRadius: 8,
          }}
          value={titleValue}
          onChange={(e) => updateProps({ title: e.target.value })}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.fontSizeLabel || 'Font size'}
        </label>
        <input
          type="number"
          className="add-column"
          min={8}
          max={200}
          value={Number.isFinite(titleSize) ? titleSize : 20}
          onChange={(e) => updateProps({ titleSize: Number(e.target.value || 0) })}
          style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {(t.colorLabel || 'Text color') + ` (${isLight ? 'Light' : 'Dark'})`}
        </label>
        <ColorPicker
          id="text-title-color"
          color={color}
          setColor={(c) => updateProps({ [getThemeColorKey('color', themeMode)]: c })}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.alignmentLabel || 'Alignment'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={alignmentOptions}
          value={alignmentOptions.find((o) => o.value === alignment) || null}
          onChange={(selectedOption) => updateProps({ alignment: selectedOption ? selectedOption.value : 'left' })}
          isClearable={true}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.fontFamilyLabel || 'Font family'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={fontFamilies}
          value={fontFamilies.find((o) => o.value === fontFamily) || fontFamilies[0]}
          onChange={(selectedOption) => updateProps({ fontFamily: selectedOption ? selectedOption.value : '' })}
          isClearable={true}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.fontWeightLabel || 'Font weight'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={fontWeights}
          value={fontWeights.find((o) => o.value === String(fontWeight)) || fontWeights[0]}
          onChange={(selectedOption) => updateProps({ fontWeight: selectedOption ? selectedOption.value : '' })}
          isClearable={true}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.fontStyleLabel || 'Font style'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={fontStyles}
          value={fontStyles.find((o) => o.value === fontStyle) || fontStyles[0]}
          onChange={(selectedOption) => updateProps({ fontStyle: selectedOption ? selectedOption.value : 'normal' })}
          isClearable={false}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
          {t.textDecorationLabel || 'Text decoration'}
        </label>
        <Select
          classNamePrefix="react-select"
          options={decorations}
          value={decorations.find((o) => o.value === textDecoration) || decorations[0]}
          onChange={(selectedOption) => updateProps({ textDecoration: selectedOption ? selectedOption.value : 'none' })}
          isClearable={false}
        />
      </div>
    </div>
  );
};

export default TextTitleSettings;
